import React, { useContext, useEffect, useMemo, useState } from 'react';
import { CartContext } from '../context/CartContext';
import { formatPhone } from '../utils/phoneUtils';
import { buildOrderPayload, buildQuotePayload } from '../utils/orderPayload';
import Button from './ui/Button';
import Skeleton from './ui/Skeleton';

const API_URL = process.env.REACT_APP_API_URL;

const PaymentLabel = { PIX: 'PIX', CREDIT_CARD: 'Cartão de Crédito', BOLETO: 'Boleto', OTHER: 'Outro' };

const OrderSummary = ({ formData, onBack, onOrderComplete }) => {
  const { cart, clearCart } = useContext(CartContext);
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState('');
  const [orderConfirmed, setOrderConfirmed] = useState(false);
  const [confirmedOrder, setConfirmedOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const zipDigits = (formData.zipCode || '').replace(/\D/g, '');
  const cartSignature = useMemo(
    () => cart.map((item) => `${item.product.id}:${item.quantity}:${item.sizeLabel || ''}`).join('|'),
    [cart]
  );

  // O total oficial vem do backend; recalcula quando itens/forma/CEP mudam.
  useEffect(() => {
    if (cart.length === 0) {
      setQuote(null);
      return undefined;
    }
    let active = true;
    setQuoteError('');

    const timer = setTimeout(() => {
      fetch(`${API_URL}/api/orders/quote/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          buildQuotePayload(cart, {
            shippingType: formData.shippingType,
            zipCode: formData.zipCode,
          })
        ),
      })
        .then((response) => {
          if (!response.ok) throw new Error('quote falhou');
          return response.json();
        })
        .then((data) => {
          if (active) setQuote(data);
        })
        .catch(() => {
          if (active) {
            setQuote(null);
            setQuoteError('Não foi possível calcular o total agora. Tente novamente.');
          }
        });
    }, 250);

    return () => {
      active = false;
      clearTimeout(timer);
    };
    // as deps capturam exatamente o que muda o quote (itens+qtde, entrega, CEP);
    // trocar imagens ou outros campos do form não deve re-calcular
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartSignature, formData.shippingType, zipDigits]);

  const handleConfirmOrder = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/orders/`, {
        method: 'POST',
        body: buildOrderPayload(formData, cart),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail || 'Erro ao criar pedido. Verifique os dados e tente novamente.'
        );
      }

      const order = await response.json();
      setConfirmedOrder(order);
      setOrderConfirmed(true);
      clearCart();
    } catch (err) {
      setError(err.message || 'Erro ao processar pedido. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  if (orderConfirmed && confirmedOrder) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream-100 px-4">
        <div className="w-full max-w-md rounded-card bg-white p-8 text-center shadow-lift">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-sage-600">
            <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="font-display text-3xl font-semibold tracking-tight text-ink">
            Pedido confirmado!
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-ink-soft">
            Recebemos seu pedido. Você receberá a confirmação do pagamento e as
            atualizações no WhatsApp.
          </p>
          <div className="mt-6 rounded-xl border border-cream-200 bg-cream-50 p-4 text-sm">
            <p className="text-ink-soft">
              <strong>Pedido:</strong> #{confirmedOrder.id}
            </p>
            <p className="mt-1 text-ink-soft">
              <strong>Total:</strong>{' '}
              <span className="font-bold text-cocoa-800">
                R$ {parseFloat(confirmedOrder.total_price).toFixed(2)}
              </span>
            </p>
          </div>
          <Button
            className="mt-6 w-full"
            onClick={() => onOrderComplete(confirmedOrder.id, confirmedOrder.token)}
          >
            Acompanhar Pedido
          </Button>
        </div>
      </div>
    );
  }

  const showCarrierNote =
    quote &&
    formData.shippingType === 'DELIVERY' &&
    zipDigits.length === 8 &&
    !zipDigits.startsWith('75');

  return (
    <div className="min-h-screen bg-cream-100 px-4 py-8">
      <div className="mx-auto max-w-4xl">
        <button
          onClick={onBack}
          className="mb-6 flex items-center gap-2 text-ink-soft transition-colors hover:text-ink"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          Editar Dados
        </button>

        <div className="rounded-card bg-white p-6 shadow-lift sm:p-8">
          <h2 className="mb-6 font-display text-3xl font-semibold tracking-tight text-ink">
            Resumo do Pedido
          </h2>

          <div className="mb-8 grid grid-cols-1 gap-8 lg:grid-cols-2">
            <div>
              <h3 className="mb-4 font-semibold text-ink">Dados do Pedido</h3>
              <div className="space-y-2 rounded-xl bg-cream-50 p-4 text-sm text-ink-soft">
                <p><strong>Telefone:</strong> {formatPhone(formData.phone)}</p>
                <p><strong>Pagamento:</strong> {PaymentLabel[formData.paymentMethod] || formData.paymentMethod}</p>
                <p>
                  <strong>Entrega:</strong>{' '}
                  {formData.shippingType === 'PICKUP' ? 'Retirada no local' : 'Entrega'}
                </p>
                {formData.notes && <p><strong>Observações:</strong> {formData.notes}</p>}
              </div>

              {formData.shippingType === 'DELIVERY' && (
                <div className="mt-4">
                  <h4 className="mb-3 font-semibold text-ink">Endereço de Entrega</h4>
                  <div className="space-y-2 rounded-xl bg-cream-50 p-4 text-sm text-ink-soft">
                    {formData.recipientName && <p><strong>Destinatário:</strong> {formData.recipientName}</p>}
                    <p>
                      <strong>Endereço:</strong> {formData.street}, {formData.number}
                      {formData.complement && ` - ${formData.complement}`}
                    </p>
                    <p><strong>Bairro:</strong> {formData.neighborhood}</p>
                    <p><strong>Cidade:</strong> {formData.city} - {formData.state}</p>
                    <p><strong>CEP:</strong> {formData.zipCode}</p>
                  </div>
                </div>
              )}
            </div>

            <div>
              <h3 className="mb-4 font-semibold text-ink">Itens do Pedido</h3>
              {quote ? (
                <ul className="space-y-3">
                  {quote.lines.map((line, index) => {
                    const cartItem = cart[index];
                    const image = cartItem?.customImage || cartItem?.product?.image;
                    return (
                      <li key={`${line.product_id}-${index}`} className="flex gap-4 rounded-xl bg-cream-50 p-4">
                        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-cream-100">
                          {image ? (
                            <img src={image} alt={line.name} className="h-full w-full object-cover" />
                          ) : null}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="truncate text-sm font-semibold text-ink">{line.name}</h4>
                          <p className="mt-0.5 text-xs text-ink-muted">
                            {line.quantity} × R$ {Number(line.unit_price).toFixed(2)}
                          </p>
                          {line.bundle_applied && (
                            <p className="mt-1 text-xs font-semibold text-sage-600">
                              Desconto por quantidade aplicado
                            </p>
                          )}
                        </div>
                        <p className="whitespace-nowrap text-sm font-bold text-cocoa-800">
                          R$ {Number(line.total).toFixed(2)}
                        </p>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="space-y-3" aria-busy={!quoteError}>
                  {quoteError ? (
                    <p className="rounded-xl bg-honey-50 p-3 text-sm text-honey-700">{quoteError}</p>
                  ) : (
                    <>
                      <Skeleton className="h-24 w-full rounded-xl" />
                      <Skeleton className="h-24 w-full rounded-xl" />
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-cocoa-100 pt-6">
            <div className="mb-6 rounded-xl bg-cream-50 p-6">
              {quote ? (
                <>
                  <div className="mb-2 flex justify-between text-sm text-ink-soft">
                    <span>Subtotal</span>
                    <span>R$ {Number(quote.subtotal).toFixed(2)}</span>
                  </div>
                  {Number(quote.discount) > 0 && (
                    <div className="mb-2 flex justify-between border-b border-cream-200 pb-2 text-sm font-medium text-sage-600">
                      <span>Desconto</span>
                      <span>- R$ {Number(quote.discount).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="mb-2 flex justify-between text-sm text-ink-soft">
                    <span>Frete</span>
                    <span>
                      {Number(quote.shipping_fee) > 0
                        ? `R$ ${Number(quote.shipping_fee).toFixed(2)}`
                        : formData.shippingType === 'DELIVERY'
                        ? 'A combinar (transportadora)'
                        : 'Retirada no local'}
                    </span>
                  </div>
                  <div className="mt-3 flex items-center justify-between border-t border-cream-200 pt-3">
                    <span className="font-semibold text-ink">Total</span>
                    <span className="font-display text-3xl font-semibold text-cocoa-800">
                      R$ {Number(quote.total).toFixed(2)}
                    </span>
                  </div>
                  {showCarrierNote && (
                    <p className="mt-3 text-xs font-medium text-honey-700">
                      O valor do frete por transportadora será confirmado após o fechamento.
                    </p>
                  )}
                  {!quote.minimum_order_met && (
                    <p
                      role="alert"
                      className="mt-3 rounded-xl border border-honey-200 bg-honey-50 p-3 text-xs font-medium text-honey-700"
                    >
                      Pedido mínimo de R$ {Number(quote.minimum_order_total).toFixed(2)}.
                      Faltam R$ {Number(quote.minimum_order_remaining).toFixed(2)} em produtos.
                    </p>
                  )}
                </>
              ) : (
                <div className="space-y-3" aria-busy>
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-8 w-full" />
                </div>
              )}
            </div>

            {error && (
              <div role="alert" className="mb-4 rounded-xl border border-clay-200 bg-clay-50 p-4">
                <p className="text-sm text-clay-700">{error}</p>
              </div>
            )}

            <Button
              className="w-full text-base"
              size="lg"
              onClick={handleConfirmOrder}
              loading={isLoading}
              disabled={!quote || !quote.minimum_order_met}
            >
              {isLoading ? 'Processando…' : 'Confirmar Pedido'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
